export type KitchenOrder = {
  _id: string;
  revision?: number;
  status: string;
  [key: string]: any;
};

type OrderRecord = {
  revision: number;
  order: KitchenOrder | null;
  changedAt: number;
};

export type KitchenOrdersState = {
  eventSeq: number;
  records: Map<string, OrderRecord>;
};

const revisionOf = (order: KitchenOrder) => order.revision ?? 0;
const isTerminal = (order: KitchenOrder) => order.status === 'entregado' || order.status === 'cancelado';

export const createKitchenOrdersState = (): KitchenOrdersState => ({ eventSeq: 0, records: new Map() });

export const applyKitchenOrderEvent = (state: KitchenOrdersState, order: KitchenOrder): KitchenOrdersState => {
  const eventSeq = state.eventSeq + 1;
  const existing = state.records.get(order._id);
  if (existing && revisionOf(order) <= existing.revision) {
    return { ...state, eventSeq };
  }

  const records = new Map(state.records);
  records.set(order._id, {
    revision: revisionOf(order),
    order: isTerminal(order) ? null : order,
    changedAt: eventSeq,
  });
  return { eventSeq, records };
};

// A snapshot is authoritative for orders that did not change while the request
// was in flight. Per-order event sequence numbers preserve newer socket events.
export const reconcileKitchenSnapshot = (
  state: KitchenOrdersState,
  snapshot: KitchenOrder[],
  requestEventSeq: number,
): KitchenOrdersState => {
  const incoming = new Map(snapshot.map(order => [order._id, order]));
  const records = new Map(state.records);

  state.records.forEach((existing, id) => {
    const order = incoming.get(id);
    if (existing.changedAt > requestEventSeq) {
      incoming.delete(id);
      return;
    }

    if (!order) {
      records.set(id, { ...existing, order: null, changedAt: requestEventSeq });
      return;
    }
    incoming.delete(id);

    const revision = revisionOf(order);
    if (existing.order === null && revision <= existing.revision) return;
    if (revision < existing.revision) return;
    records.set(id, { revision, order: isTerminal(order) ? null : order, changedAt: requestEventSeq });
  });

  incoming.forEach((order, id) => {
    const revision = revisionOf(order);
    records.set(id, { revision, order: isTerminal(order) ? null : order, changedAt: requestEventSeq });
  });

  return { ...state, records };
};

export const activeKitchenOrders = (state: KitchenOrdersState) =>
  Array.from(state.records.values()).flatMap(record => record.order ? [record.order] : []);
